import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Cell,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ExpandedState,
  type Header,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, ChevronsUpDown, ChevronDown, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/feedback/EmptyState';
import { cn } from '@/lib/utils/cn';
import type { Table as TanstackTable } from '@tanstack/react-table';
import { exportTableAsCsv } from './csv-export';
import { DataTableToolbar } from './DataTableToolbar';
import { useTablePrefs, type TablePrefs } from './use-table-prefs';
import {
  attachNumericColumnFilters,
  DataTableColumnFilter,
  DataTableFilterChips,
  getColumnFilterKind,
} from './DataTableColumnFilter';

interface DataTableProps<TData, TValue> {
  /** Stable identifier — used as the localStorage key for persisted prefs. */
  tableId: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  searchColumnId?: string;
  disableSearch?: boolean;
  /**
   * Replaces the search box entirely with custom content (e.g. a
   * page-specific search + action buttons), rendered in the same row as
   * Show / Density / Columns / Export instead of stacking above/below it.
   * Implies `disableSearch`.
   */
  leftContent?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  pageSize?: number;
  toolbar?: React.ReactNode;
  renderSubRow?: (row: TData) => React.ReactNode;
  /** When set, a selection checkbox column is prepended. */
  enableRowSelection?: boolean;
  /** Render a bulk action bar when rows are selected. */
  renderSelectionActions?: (selectedIds: string[], clear: () => void) => React.ReactNode;
  /** Hook called when a row is clicked (e.g. navigate to detail). */
  onRowClick?: (row: TData) => void;
  /** Resolve a unique id per row (defaults to the row index). */
  getRowId?: (row: TData, index: number) => string;
  /** Disable CSV export button. */
  disableExport?: boolean;
  /** Filename for CSV download. */
  exportFilename?: string;
  /** Replace the default CSV export (visible columns, raw values). */
  onExport?: (table: TanstackTable<TData>) => void;
  /** Export button text (default "Export"). */
  exportLabel?: string;
  /**
   * Default column visibility on first render. Users can still toggle
   * any column on/off via the Columns dropdown; their preferences are
   * persisted per `tableId`. This map only seeds the FIRST visit.
   */
  initialColumnVisibility?: VisibilityState;
  /**
   * Pin columns to the left or right edge so they remain visible while
   * the rest of the table scrolls horizontally. Pinned columns are
   * excluded from drag-reorder.
   */
  initialColumnPinning?: ColumnPinningState;
  /**
   * Renders between the toolbar and the table — useful for feature-
   * specific filter rows that should sit visually above the table but
   * below the table controls (search / page size / density / columns).
   */
  beforeTable?: React.ReactNode;
  /** Default sort applied on first render (e.g. newest rows first). */
  initialSorting?: SortingState;
  /** Fires whenever search / column filters change the visible row set. */
  onFilteredDataChange?: (rows: TData[]) => void;
  serverPagination?: {
    pageIndex: number;
    pageSize: number;
    rowCount: number;
    onChange: (next: { pageIndex: number; pageSize: number }) => void;
  };
}

export function DataTable<TData, TValue>({
  tableId,
  columns: userColumns,
  data,
  isLoading = false,
  searchPlaceholder,
  searchColumnId,
  disableSearch = false,
  leftContent,
  emptyTitle = 'No records',
  emptyDescription = 'Nothing to show with the current filters.',
  pageSize: pageSizeProp = 25,
  toolbar,
  renderSubRow,
  enableRowSelection = false,
  renderSelectionActions,
  onRowClick,
  getRowId,
  disableExport = false,
  exportFilename = 'export.csv',
  onExport,
  exportLabel,
  initialColumnVisibility,
  initialColumnPinning,
  beforeTable,
  initialSorting,
  onFilteredDataChange,
  serverPagination,
}: DataTableProps<TData, TValue>) {
  const [prefs, setPrefs] = useTablePrefs(tableId);

  const [sorting, setSorting] = React.useState<SortingState>(initialSorting ?? []);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: prefs.pageSize ?? pageSizeProp,
  });

  // Build the columns with optional selection column.
  const hasSubRow = Boolean(renderSubRow);
  const columns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    const expanderCol: ColumnDef<TData, TValue>[] = hasSubRow
      ? [
          {
            id: 'expand',
            size: 36,
            enableHiding: false,
            enableSorting: false,
            enableResizing: false,
            enableGlobalFilter: false,
            header: () => null,
            cell: ({ row }) => (
              <button
                type="button"
                aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
                onClick={(e) => {
                  e.stopPropagation();
                  row.toggleExpanded();
                }}
                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ),
          },
        ]
      : [];

    if (!enableRowSelection) return [...expanderCol, ...attachNumericColumnFilters(userColumns)];
    return [
      ...expanderCol,
      {
        id: 'select',
        size: 36,
        enableHiding: false,
        enableSorting: false,
        enableResizing: false,
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all"
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(Boolean(v))}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label="Select row"
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(Boolean(v))}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      ...attachNumericColumnFilters(userColumns),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableRowSelection, hasSubRow, userColumns]);

  // TanStack uses `accessorKey` as the column id when no explicit `id` is set.
  // Reading accessorKey too (not just `.id`) produces the correct order —
  // otherwise TanStack ignores most entries and applies only the
  // explicit-id columns, leaving everything else in natural order.
  const columnId = (c: ColumnDef<TData, TValue>, i: number) => {
    const def = c as { id?: string; accessorKey?: string };
    if (typeof def.id === 'string') return def.id;
    if (typeof def.accessorKey === 'string') return def.accessorKey;
    return `col-${i}`;
  };

  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(() => {
    if (prefs.columnOrder?.length) {
      // Prefs saved before this table gained an expander / selection column
      // would not list it, and TanStack appends unlisted columns to the END —
      // parking the control column on the far right. Pin them back to front.
      const leading = ['expand', 'select'].filter(
        (id) => columns.some((c) => (c as { id?: string }).id === id) &&
          !prefs.columnOrder!.includes(id),
      );
      return leading.length ? [...leading, ...prefs.columnOrder] : prefs.columnOrder;
    }
    // TanStack uses `accessorKey` as the column id when no explicit `id` is set.
    // The previous version only read `.id` and fell back to "col-N" placeholders,
    // which didn't match the real column ids → TanStack ignored most entries
    // and applied only the explicit-id columns, leaving everything else in
    // natural order. Reading accessorKey too produces the correct order.
    return columns.map((c, i) => {
      const def = c as { id?: string; accessorKey?: string };
      if (typeof def.id === 'string') return def.id;
      if (typeof def.accessorKey === 'string') return def.accessorKey;
      return `col-${i}`;
    });
  });

  // Columns can appear/disappear after mount — e.g. a role-gated leading
  // column (row-select) that isn't known yet on the very first render
  // because the role check resolves a beat later. `columnOrder` above is a
  // one-time snapshot, so a column that shows up afterward would otherwise
  // be silently dropped (or picked up out of position) since TanStack scopes
  // ordering strictly to the ids it already knows about. Keep it in sync:
  // drop ids for columns that vanished, and splice newly-appeared ids in at
  // the position they naturally occupy, so e.g. a leading checkbox column
  // lands at the front instead of getting tacked on at the end.
  React.useEffect(() => {
    const currentIds = columns.map(columnId);
    setColumnOrder((prev) => {
      const currentSet = new Set(currentIds);
      let next = prev.filter((id) => currentSet.has(id));
      currentIds.forEach((id, naturalIdx) => {
        if (next.includes(id)) return;
        let insertAt = next.length;
        for (let i = naturalIdx - 1; i >= 0; i--) {
          const neighborId = currentIds[i];
          if (neighborId === undefined) continue;
          const idx = next.indexOf(neighborId);
          if (idx !== -1) {
            insertAt = idx + 1;
            break;
          }
        }
        if (naturalIdx === 0) insertAt = 0;
        next = [...next.slice(0, insertAt), id, ...next.slice(insertAt)];
      });
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    prefs.columnVisibility ?? initialColumnVisibility ?? {},
  );
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(
    prefs.columnSizing ?? {},
  );
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(
    initialColumnPinning ?? { left: [], right: [] },
  );
  const density: NonNullable<TablePrefs['density']> = prefs.density ?? 'comfortable';

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnSizing,
      columnPinning,
      rowSelection,
      expanded,
      pagination: serverPagination
        ? { pageIndex: serverPagination.pageIndex, pageSize: serverPagination.pageSize }
        : pagination,
    },
    onPaginationChange: serverPagination
      ? (updater) => {
          const cur = { pageIndex: serverPagination.pageIndex, pageSize: serverPagination.pageSize };
          const next = typeof updater === 'function' ? updater(cur) : updater;
          serverPagination.onChange(
            next.pageSize !== cur.pageSize ? { ...next, pageIndex: 0 } : next,
          );
        }
      : setPagination,
    ...(serverPagination
      ? { manualPagination: true, rowCount: serverPagination.rowCount }
      : {}),
    // Parent re-renders often pass a new `data` array (polling, clocks,
    // inline `.filter()`). TanStack's default auto-reset would bounce the
    // user back to page 1 on every such update.
    autoResetPageIndex: false,
    enableRowSelection,
    enableMultiSort: true,
    enableColumnResizing: true,
    enableColumnPinning: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => hasSubRow,
    onExpandedChange: setExpanded,
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater) => {
      setGlobalFilter(updater);
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setPrefs({ ...prefs, columnVisibility: next });
        return next;
      });
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setPrefs({ ...prefs, columnSizing: next });
        return next;
      });
    },
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinning,
    ...(getRowId ? { getRowId } : {}),
  });

  React.useEffect(() => {
    if (!onFilteredDataChange) return;
    onFilteredDataChange(table.getFilteredRowModel().rows.map((row) => row.original));
    // table is recreated each render; columnFilters / data are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, globalFilter, data, onFilteredDataChange]);

  // If a live update shrinks the dataset, keep pageIndex in range instead
  // of showing an empty page.
  React.useEffect(() => {
    if (serverPagination) return;
    const filtered = table.getFilteredRowModel().rows.length;
    const pageCount = Math.max(1, Math.ceil(filtered / pagination.pageSize) || 1);
    const maxIndex = Math.max(0, pageCount - 1);
    if (pagination.pageIndex > maxIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: maxIndex }));
    }
  }, [columnFilters, globalFilter, data.length, pagination.pageIndex, pagination.pageSize]);

  // DnD for header reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(String(active.id));
    const newIndex = columnOrder.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(columnOrder, oldIndex, newIndex);
    setColumnOrder(next);
    setPrefs({ ...prefs, columnOrder: next });
  };

  const onDensityChange = (next: NonNullable<TablePrefs['density']>) => {
    setPrefs({ ...prefs, density: next });
  };

  const selectedIds = Object.keys(rowSelection);
  const total = serverPagination ? serverPagination.rowCount : data.length;
  const pageIndex = table.getState().pagination.pageIndex;
  const ps = table.getState().pagination.pageSize;
  const pageCount = table.getPageCount();

  const cellPadding = {
    compact: 'py-2',
    comfortable: 'py-3',
    spacious: 'py-4',
  }[density];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col gap-3">
      <div className="shrink-0 space-y-3">
      <DataTableToolbar
        table={table}
        density={density}
        onDensityChange={onDensityChange}
        searchPlaceholder={searchPlaceholder ?? 'Search…'}
        disableSearch={disableSearch}
        leftContent={leftContent}
        {...(searchColumnId ? { searchColumnId } : {})}
        toolbar={toolbar}
        {...(disableExport ? {} : { onExport: () => (onExport ? onExport(table) : exportTableAsCsv(table, exportFilename)) })}
        exportLabel={exportLabel}
        pageSize={ps}
        pageSizeOptions={
          [10, 25, 50, 100].includes(pageSizeProp)
            ? [10, 25, 50, 100]
            : [pageSizeProp, 10, 25, 50, 100].sort((a, b) => a - b)
        }
        onPageSizeChange={(n) => {
          table.setPageSize(n);
          setPrefs({ ...prefs, pageSize: n });
        }}
      />

      <DataTableFilterChips table={table} />

      {beforeTable}

      {enableRowSelection && selectedIds.length > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-accent/40 px-4 py-2 text-sm">
          <span className="font-medium">
            {selectedIds.length} selected
          </span>
          <div className="flex items-center gap-2">
            {renderSelectionActions?.(selectedIds, () => setRowSelection({}))}
            <Button variant="ghost" size="sm" onClick={() => setRowSelection({})}>
              Clear
            </Button>
          </div>
        </div>
      )}
      </div>

      {/*
        Single scroll region for both axes. This is intentional: a CSS
        scroll container can't pin sticky to anything outside itself, so
        the only way to have both a sticky header AND horizontal scroll
        is to put both inside the same container. The TableHeader sticks
        to the top of THIS container; pinned columns stick to the right.
       */}
      {/*
        DndContext is HOISTED to wrap the entire table container. Keeping
        it inside <thead> (its natural home for the header-drag flow) is
        illegal HTML — dnd-kit renders a hidden aria-live <div> that
        becomes a direct child of <thead>, and React logs
        `<div> cannot appear as a child of <thead>` on every render.
        Hoisting to a plain <div> parent silences the warning while the
        SortableContext below still governs the header cells the same
        way (sensors + drag handlers are wired via React context, not
        DOM containment).
       */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
      >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="relative isolate z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card">
        <div className="min-h-0 min-w-0 flex-1 overflow-auto">
          <Table containerClassName="overflow-visible">
            {/* Sticky header — translucent primary tint + backdrop-blur
                for the glossy look. Pinned th cells use `.bg-table-header`
                (opaque compound with the same colour math) so they line
                up visually with the translucent band. */}
            <TableHeader className="sticky top-0 z-30 border-b border-border bg-primary/[0.06] backdrop-blur-sm">
                {table.getHeaderGroups().map((headerGroup) => {
                  // Only non-pinned headers participate in drag reorder; the
                  // pinned ones stay locked to their edge.
                  const sortableIds = headerGroup.headers
                    .filter((h) => !h.column.getIsPinned())
                    .map((h) => h.id);
                  return (
                    <TableRow key={headerGroup.id} className="hover:bg-transparent">
                      <SortableContext
                        items={sortableIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DraggableHeaderCell
                            key={header.id}
                            headerId={header.id}
                            pinned={header.column.getIsPinned()}
                            stickyStyle={getStickyStyle(header, 'header')}
                          >
                            {header.isPlaceholder ? null : (
                              <HeaderContent header={header}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </HeaderContent>
                            )}
                            {header.column.getCanResize() && (
                              <span
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className={cn(
                                  'absolute right-0 top-0 z-20 h-full w-1.5 cursor-col-resize select-none touch-none bg-transparent transition-colors hover:bg-primary/30',
                                  header.column.getIsResizing() && 'bg-primary/60',
                                )}
                              />
                            )}
                          </DraggableHeaderCell>
                        ))}
                      </SortableContext>
                    </TableRow>
                  );
                })}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`} className="hover:bg-transparent">
                    {table.getVisibleLeafColumns().map((c) => (
                      <TableCell key={c.id} className={cellPadding}>
                        <Skeleton className="h-4 w-3/4" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={table.getVisibleLeafColumns().length} className="p-0">
                    <EmptyState title={emptyTitle} description={emptyDescription} className="border-0" />
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row, rowIdx) => {
                  const isSelected = row.getIsSelected();
                  const isStripe = rowIdx % 2 === 1;
                  // Non-pinned cells can use translucent stripes / hover / selected
                  // tints — nothing scrolls under them.
                  // Pinned (sticky) cells MUST be fully opaque: when the user
                  // horizontally scrolls, the middle columns slide beneath the
                  // pinned column, and any translucent background lets that
                  // sliding text bleed through. We pre-mix the stripe / hover /
                  // selected tints with the solid card color via color-mix()
                  // so the pinned cell is opaque in every state.
                  const rowBg = isStripe ? 'bg-muted/30' : 'bg-card';
                  const pinnedBgSolid = isStripe
                    ? 'bg-[color-mix(in_srgb,hsl(var(--card)),hsl(var(--muted))_50%)]'
                    : 'bg-card';
                  const pinnedHoverSolid =
                    'group-hover/row:bg-[color-mix(in_srgb,hsl(var(--card)),hsl(var(--primary))_6%)]';
                  const pinnedSelectedSolid =
                    'group-data-[state=selected]/row:bg-[color-mix(in_srgb,hsl(var(--card)),hsl(var(--primary))_10%)]';
                  return (
                    <React.Fragment key={row.id}>
                    <TableRow
                      data-state={isSelected ? 'selected' : undefined}
                      onClick={() => onRowClick?.(row.original)}
                      className={cn(
                        'group/row transition-colors',
                        rowBg,
                        'hover:bg-primary/[0.06]',
                        'data-[state=selected]:bg-primary/[0.1]',
                        // Left accent bar on the selected row.
                        isSelected &&
                          'relative before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-primary',
                        onRowClick && 'cursor-pointer',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const pinned = cell.column.getIsPinned();
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              cellPadding,
                              pinned && [
                                pinnedBgSolid,
                                pinnedHoverSolid,
                                pinnedSelectedSolid,
                              ],
                            )}
                            style={{
                              width: cell.column.getSize(),
                              ...getStickyStyle(cell, 'body'),
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {renderSubRow && row.getIsExpanded() && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={row.getVisibleCells().length}
                          className="bg-muted/20 p-0"
                        >
                          {renderSubRow(row.original)}
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      </div>
      </DndContext>

      {total > 0 && (
        <div className="relative z-10 mt-3 flex shrink-0 flex-nowrap items-center justify-between gap-3 border-t border-border/60 bg-card px-1 py-3 text-xs text-muted-foreground">
          <span className="min-w-0 truncate">
            Showing{' '}
            <span className="font-medium text-foreground">
              {Math.min(total, pageIndex * ps + 1)}–{Math.min(total, (pageIndex + 1) * ps)}
            </span>{' '}
            of <span className="font-medium text-foreground">{total}</span>
          </span>
          <div className="flex shrink-0 items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="gap-1.5"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </Button>
            <span className="whitespace-nowrap tabular-nums">
              Page{' '}
              <span className="font-medium text-foreground">{pageIndex + 1}</span> /{' '}
              <span className="font-medium text-foreground">
                {Math.max(pageCount, 1)}
              </span>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="gap-1.5"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface DraggableHeaderCellProps {
  headerId: string;
  /** Pinned-edge ('left' | 'right' | false). Pinned cells aren't draggable. */
  pinned: false | 'left' | 'right';
  /** Sticky positioning style for the pinned cell. */
  stickyStyle?: React.CSSProperties;
  children: React.ReactNode;
}

function DraggableHeaderCell({
  headerId,
  pinned,
  stickyStyle,
  children,
}: DraggableHeaderCellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: headerId,
    disabled: Boolean(pinned),
  });
  const style: React.CSSProperties = pinned
    ? // stickyStyle already includes `position: 'sticky'`.
      { ...stickyStyle }
    : {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
      };
  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative whitespace-nowrap',
        // Pinned headers use `.bg-table-header` — the opaque compound
        // (bg-card + 6% primary tint layered as gradients) that the thead
        // itself uses. Sticky positioning creates a stacking context that
        // hides the parent thead's translucent bg from behind the pinned
        // cell, so we can't rely on inheritance — we have to paint the
        // same colour explicitly on the pinned cell.
        pinned && 'bg-table-header',
        'group',
      )}
    >
      {/*
       * Grip handle is positioned absolutely so it does NOT push the
       * header text rightward. Without this, the header content sits
       * ~22px further right than the body cell content (button width
       * + gap), making columns look asymmetric. On hover the grip
       * fades in over the left padding gutter (px-3 = 12px) and the
       * text stays visible because the icon is only 14px wide.
       */}
      {!pinned && (
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="absolute left-1 top-1/2 -translate-y-1/2 cursor-grab rounded text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100 active:cursor-grabbing"
          aria-label={`Reorder column ${headerId}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="flex items-center gap-1.5">{children}</div>
    </TableHead>
  );
}

interface HeaderContentProps<TData, TValue> {
  header: Header<TData, TValue>;
  children: React.ReactNode;
}

function HeaderContent<TData, TValue>({ header, children }: HeaderContentProps<TData, TValue>) {
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const onClick = header.column.getToggleSortingHandler();
  const filterKind = getColumnFilterKind(header.column);

  const label = canSort ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 hover:text-foreground',
        sorted && 'text-foreground',
      )}
    >
      {children}
      {sorted === 'asc' ? (
        <ArrowUp className="h-3 w-3" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="h-3 w-3" />
      ) : (
        <ChevronsUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  ) : (
    <span>{children}</span>
  );

  if (!filterKind) return label;

  return (
    <div className="inline-flex items-center gap-0.5">
      {label}
      <DataTableColumnFilter column={header.column} kind={filterKind} />
    </div>
  );
}

/**
 * Compute the sticky positioning style for a pinned header/cell. TanStack
 * tracks the cumulative pinned column widths so we can derive the right
 * (or left) offset directly. Adds a subtle inset shadow on the leading
 * edge of the leftmost/rightmost pinned column to visually separate it
 * from the scrolling area.
 */
function getStickyStyle<TData, TValue>(
  cellOrHeader: Cell<TData, TValue> | Header<TData, TValue>,
  variant: 'header' | 'body',
): React.CSSProperties | undefined {
  const pinned = cellOrHeader.column.getIsPinned();
  if (!pinned) return undefined;
  const isFirstRight =
    pinned === 'right' && cellOrHeader.column.getIsFirstColumn('right');
  const isLastLeft =
    pinned === 'left' && cellOrHeader.column.getIsLastColumn('left');
  const shadow = isFirstRight
    ? '-4px 0 6px -4px hsl(var(--foreground) / 0.12)'
    : isLastLeft
      ? '4px 0 6px -4px hsl(var(--foreground) / 0.12)'
      : undefined;
  return {
    position: 'sticky',
    left: pinned === 'left' ? `${cellOrHeader.column.getStart('left')}px` : undefined,
    right: pinned === 'right' ? `${cellOrHeader.column.getAfter('right')}px` : undefined,
    // Header sticky-row is z-30, regular body cells are z-0. Pinned body
    // needs to clear the regular body cells (z-20) so scrolled content
    // hides correctly; pinned header needs to clear the rest of the
    // sticky header row (z-40).
    zIndex: variant === 'header' ? 40 : 20,
    boxShadow: shadow,
  };
}
